// AIDEV-NOTE: Monta a arvore de navegacao do catalogo (Secao > Grupo > Subgrupo) a partir das
// linhas achatadas de GET /produtos/arvore. Duas regras de produto moram aqui:
//   1. O nivel Secao so existe quando ha 2+ secoes distintas — nao gastar um toque em nivel
//      que nao classifica nada (cliente que nao preenche SECAO no ERP comeca nos grupos).
//   2. Produto com campo em branco vira no "Sem grupo"/"Sem subgrupo" com valor '' — o filtro
//      da API trata '' como "sem filtro", entao esses nos caem na listagem do nivel acima.

export interface NoCatalogoApi {
  secao: string | null;
  grupo: string | null;
  subgrupo: string | null;
  /** Quantidade de produtos distintos sob a combinacao */
  total: number;
}

export type NivelArvore = 'secao' | 'grupo' | 'subgrupo';

export interface NoArvore {
  /** Texto exibido — o valor do ERP ou o rotulo de "sem classificacao" */
  label: string;
  /** Valor enviado ao filtro da API ('' quando o campo vem vazio do ERP) */
  valor: string;
  nivel: NivelArvore;
  total: number;
  /** Vazio = folha: clicar aqui ja lista os produtos */
  filhos: NoArvore[];
}

export interface Arvore {
  /** Niveis em jogo, na ordem — 'secao' some quando nao classifica nada */
  niveis: NivelArvore[];
  raizes: NoArvore[];
}

const LABEL_SEM_VALOR: Record<NivelArvore, string> = {
  secao: 'Sem seção',
  grupo: 'Sem grupo',
  subgrupo: 'Sem subgrupo'
};

function valorDoNivel(linha: NoCatalogoApi, nivel: NivelArvore): string {
  return (linha[nivel] ?? '').trim();
}

function baldesDistintos(linhas: NoCatalogoApi[], nivel: NivelArvore): number {
  return new Set(linhas.map((l) => valorDoNivel(l, nivel))).size;
}

// AIDEV-NOTE: Nos sem classificacao ("Sem grupo") vao para o fim — o vendedor procura
// primeiro pelo nome real; o balde residual nao deve disputar o topo da lista.
function ordenarNos(nos: NoArvore[]): NoArvore[] {
  return nos.sort((a, b) => {
    if (!a.valor !== !b.valor) return a.valor ? -1 : 1;
    return a.label.localeCompare(b.label, 'pt-BR');
  });
}

// AIDEV-NOTE: recebe os niveis RESTANTES (niveis[0] = nivel atual) — passar so o proximo fazia a
// recursao parar no segundo nivel e sumia com os subgrupos de quem usa secao.
function agrupar(linhas: NoCatalogoApi[], niveis: NivelArvore[]): NoArvore[] {
  const [nivel, ...resto] = niveis;
  if (!nivel) return [];
  const proximo = resto[0];

  const porValor = new Map<string, NoCatalogoApi[]>();
  for (const linha of linhas) {
    const valor = valorDoNivel(linha, nivel);
    const existentes = porValor.get(valor);
    if (existentes) existentes.push(linha);
    else porValor.set(valor, [linha]);
  }

  const nos: NoArvore[] = [];
  for (const [valor, linhasDoNo] of porValor) {
    // AIDEV-NOTE: Grupo sempre aparece (e o nivel que o vendedor navega). Ja o subgrupo com um
    // balde so nao discrimina nada: vira folha e o clique no grupo ja lista os produtos,
    // evitando o "BEBIDAS > BEBIDAS > produtos".
    const vaiTerFilhos =
      proximo !== undefined && (proximo !== 'subgrupo' || baldesDistintos(linhasDoNo, proximo) > 1);
    nos.push({
      label: valor || LABEL_SEM_VALOR[nivel],
      valor,
      nivel,
      total: linhasDoNo.reduce((soma, l) => soma + l.total, 0),
      filhos: vaiTerFilhos ? agrupar(linhasDoNo, resto) : []
    });
  }

  return ordenarNos(nos);
}

export function montarArvore(linhas: NoCatalogoApi[]): Arvore {
  const comSecao = baldesDistintos(linhas, 'secao') > 1;
  const niveis: NivelArvore[] = comSecao ? ['secao', 'grupo', 'subgrupo'] : ['grupo', 'subgrupo'];

  return { niveis, raizes: agrupar(linhas, niveis) };
}

export interface CaminhoResolvido {
  /** Nos percorridos ate aqui — alimenta o breadcrumb */
  trilha: NoArvore[];
  /** Nos a exibir; vazio significa folha (hora de listar os produtos) */
  nos: NoArvore[];
}

// AIDEV-NOTE: caminho e um array de VALORES, nao de labels — '' identifica o balde
// "Sem grupo". Caminho que nao existe mais (sync trocou os grupos) degrada para a raiz
// em vez de estourar: ele sobrevive no store entre os steps do wizard.
export function resolverCaminho(arvore: Arvore, caminho: string[]): CaminhoResolvido {
  const trilha: NoArvore[] = [];
  let nos = arvore.raizes;

  for (const valor of caminho) {
    const encontrado = nos.find((n) => n.valor === valor);
    if (!encontrado) return { trilha: [], nos: arvore.raizes };
    trilha.push(encontrado);
    nos = encontrado.filhos;
  }

  return { trilha, nos };
}
